import {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ajax } from 'rxjs/ajax';

import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import EmailOutlined from '@mui/icons-material/EmailOutlined';

import { determineDataUrls } from '../shared/utils/app-utils';
import { LOGIN_AUTH0_PATH } from '../shared/constants';

/**
 * A custom hook to manage MFA (Multi-Factor Authentication) factors.
 * It handles fetching, processing, adding, and deleting MFA factors.
 */
export default function useMfaManagement() {
  const {
    isAuthenticated, isLoading, getAccessTokenSilently,
  } = useAuth0();
  const [mfaList, setMfaList] = useState([]);
  const [isMfaLoading, setIsMfaLoading] = useState(true);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const getMfaList = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        cacheMode: 'on',
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });
      const { gatewayExtRoot } = determineDataUrls();
      const gatewayExtAuth0Root = gatewayExtRoot.replace(
        '/external/argon-ext', '/auth0/external/acs'
      );
      const url = `${gatewayExtAuth0Root}auth0/user/list-mfa`;

      ajax({
        url,
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      }).subscribe({
        next: (response) => setMfaList(response.response),
        error: () => setIsMfaLoading(false),
        complete: () => setIsMfaLoading(false),
      });
    } catch (error) { setIsMfaLoading(false); }
  }, [getAccessTokenSilently]);

  const processedMfaList = useMemo(() => {
    if (!mfaList || mfaList.length === 0) {
      return [];
    }

    const phoneMfa = mfaList.find((mfa) => mfa.name === 'sms' || mfa.name === 'voice');
    const otherMfas = mfaList.filter((mfa) => mfa.name !== 'sms' && mfa.name !== 'voice');

    const mapMfa = (mfa) => ({
      id: mfa.id,
      name: mfa.name,
      lastAuthAt: mfa.last_authentication,
      icon: mfa.type === 'TOTP' ? <TextsmsOutlinedIcon /> : <EmailOutlined />,
    });

    const result = [];
    if (phoneMfa) {
      result.push({
        id: phoneMfa.id,
        name: 'Phone Message',
        icon: <ChatBubbleOutlineRounded />,
        lastAuthAt: phoneMfa.last_authentication,
      });
    }
    result.push(...otherMfas.map(mapMfa));

    return result.sort((a, b) => {
      if (!a.lastAuthAt) return 1;
      if (!b.lastAuthAt) return -1;
      return new Date(b.lastAuthAt) - new Date(a.lastAuthAt);
    });
  }, [mfaList]);

  useEffect(() => {
    if (isAuthenticated) {
      getMfaList();
    }

    if (!isLoading && !isAuthenticated) {
      const loginRedirect = `${window.location.origin}${LOGIN_AUTH0_PATH}`;
      window.location.replace(loginRedirect);
    }
  }, [getMfaList, isAuthenticated, isLoading]);

  const handleAddMfa = useCallback(async () => {
    setIsEnrolling(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const { gatewayExtRoot } = determineDataUrls();
      const gatewayExtAuth0Root = gatewayExtRoot.replace(
        '/external/argon-ext', '/auth0/external/acs'
      );
      const url = `${gatewayExtAuth0Root}auth0/user/invite-mfa?sendEmail=false`;
      ajax({
        url, method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text',
        withCredentials: true,
      }).subscribe({
        next: (response) => window.location.replace(response.response),
        error: () => setIsEnrolling(false),
      });
    } catch (error) {
      setIsEnrolling(false);
    }
  }, [getAccessTokenSilently]);

  const handleDeleteMfa = useCallback(async (mfaId, password) => {
    setDeleteError(null); // Clear previous errors
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const { gatewayExtRoot } = determineDataUrls();
      const gatewayExtAuth0Root = gatewayExtRoot.replace(
        '/external/argon-ext', '/auth0/external/acs'
      );
      const url = `${gatewayExtAuth0Root}auth0/user/mfa`;
      ajax({
        url,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
        body: { mfaId, password },
      }).subscribe({
        next: () => getMfaList(),
        error: (error) => {
          const detail = error?.response?.message || error?.message;
          const message = 'We were unable to remove the selected authenticator' +
            (detail ? ` due to ${detail}.` : '.') + ' Please try again.';
          setDeleteError(message);
        },
      });
    } catch (error) {
      setDeleteError('An unexpected error occurred.');
    }
  }, [getAccessTokenSilently, getMfaList]);

  return {
    isMfaLoading,
    isEnrolling,
    processedMfaList,
    handleAddMfa,
    handleDeleteMfa,
    deleteError,
    clearDeleteError: () => setDeleteError(null),
  };
}
